'use client'

import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'

import type { UserRoles } from '@/lib/can-user-view'

// TODO: don't show if preview token is present?
export default function GatedBlocker({
  roleRequired,
  userRole = 'anonymous',
  children,
  customLabel = null,
  customTitle = null,
  customMessage = null,
  customActions = null,
}: {
  roleRequired: UserRoles | false
  userRole: UserRoles
  children?: React.ReactNode
  customLabel?: string | React.ReactNode
  customTitle?: string | React.ReactNode
  customMessage?: string | React.ReactNode
  customActions?: React.ReactNode
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const queryString = searchParams.toString()

  const currentUrl = `${process.env.NEXT_PUBLIC_FRONTEND_URL}${pathname}`
  const loginRedirect = `${currentUrl}?${queryString}${queryString ? '&' : ''}action=login`
  const registerRedirect = `${currentUrl}?${queryString}${queryString ? '&' : ''}action=register`

  let title = null
  let message = null
  let actions = null

  if (userRole === 'anonymous' || roleRequired === 'guest') {
    title = 'Access our insightful content'

    message = <p>Create a free web account with your work email, or login to your existing one.</p>

    actions = (
      <>
        <a
          href={`/auth/login?auth-action=login&returnTo=${encodeURIComponent(loginRedirect)}`}
          className="btn btn--solid btn--large mx-0 !mb-0 min-w-[140px]"
          rel="nofollow">
          Login
        </a>

        <a
          href={`/auth/login?auth-action=register&returnTo=${encodeURIComponent(registerRedirect)}`}
          className="btn btn--outline btn--large mx-0 !mb-0 min-w-[140px]"
          rel="nofollow">
          Register
        </a>
      </>
    )
  } else if (roleRequired === 'full') {
    title = 'Exclusive content only for Jersey Finance Members'

    message = (
      <>
        <p>
          If you are already a Member (check our{' '}
          <Link href="/ifc/business-directory/">Business Directory</Link> for a full list of
          Members), you can create a free web account with your work email to read this content now.
        </p>
        <p>Not currently a Member? Learn how we can support your business.</p>
      </>
    )

    actions = (
      <>
        <a
          href={`/auth/login?auth-action=register&returnTo=${encodeURIComponent(registerRedirect)}`}
          className="btn btn--solid btn--large mx-0 !mb-0 min-w-[140px]"
          rel="nofollow">
          Register
        </a>

        <Link
          href="/membership/"
          className="btn btn--outline btn--large mx-0 !mb-0 min-w-[140px]"
          aria-label="Learn how we can support your business.">
          Learn More
        </Link>
      </>
    )
  } else if (roleRequired === 'ceo') {
    title = 'Insights exclusive to Jersey Finance CEO Connect Members'

    message = (
      <>
        <p>
          If you are already a Member of CEO Connect, create a free web account with your work email
          to read this content now.
        </p>
        <p>Not a CEO Connect Member? Learn about the benefits of upgrading.</p>
      </>
    )

    actions = (
      <>
        <a
          href={`/auth/login?auth-action=register&returnTo=${encodeURIComponent(registerRedirect)}`}
          className="btn btn--solid btn--large mx-0 !mb-0 min-w-[140px]"
          rel="nofollow">
          Register with my work email
        </a>

        <Link
          href="/membership/premium/"
          aria-label="Learn about the benefits of upgrading"
          className="btn btn--outline btn--large mx-0 !mb-0 min-w-[140px]"
          rel="nofollow">
          Learn More
        </Link>
      </>
    )
  }

  if (customTitle) {
    title = customTitle
  }

  if (customMessage) {
    message = customMessage
  }

  if (customActions) {
    actions = customActions
  }

  return (
    <section className="section bg-theme text-fg theme--primary-50">
      <div className="container">
        {children}

        <div className="mx-auto my-l w-full overflow-hidden rounded-md bg-theme p-px gradient-angle-[to_right] theme--gradient-light gradient--border @m-up:max-w-[var(--container-s-max-w)]">
          <div className="w-full rounded-md bg-theme p-l px-xl py-xxl text-center text-fg theme--base-0 s:p-m">
            {customLabel ? <span className="p--small mb-s block">{customLabel}</span> : null}

            <h2>{title}</h2>

            {message ? <div className="ms-up:p--large mb-m">{message}</div> : null}

            <div className="flex gap-s ms:flex-col ms:gap-xs ms-up:items-center ms-up:justify-center">
              {actions}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
